import {
    Toolbar,
    ToolbarGroup,
    ToolbarButton,
    Button,
    Input,
    InputOnChangeData,
    mergeClasses,
    Dropdown,
    Option,
    Popover,
    PopoverTrigger,
    PopoverSurface,
    Link,
    Checkbox,
    Label,
    Badge,
    Tooltip,
    Switch,
    Spinner,
} from '@fluentui/react-components';
import {
    Dismiss24Regular,
    Search24Regular,
    CheckmarkCircle24Regular,
    Filter24Regular,
    Circle12Filled,
    WindowBulletListFilled,
    ArrowForwardRegular,
} from '@fluentui/react-icons';
import * as React from 'react';
import { useCallback, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useSelector } from '../../../store/hooks';
import { ShiftPeriod, getPreviousShift } from '../../../utils/shiftTimes';
import {
    selectActiveConversationsSelectedIds,
    setUnreadOnly,
    selectActiveConversations,
    setSearchFlightQuery,
    setStatusFilter,
    setCurrentPage,
    setAwaitingReplyOnly,
    setInitiatedFromFilter,
    markConversationsResolved,
    selectMarkingAsResolvedId,
    setMarkingAsResolvedId,
    fetchSummary,
    clearSummary,
    selectSummary,
    setArrivingSoonOnly,
} from '../../../store/features/activeConversations/activeConversationsSlice';
import { selectConfig } from '../../../store/features/config/configSlice';
import { FeatureFlags } from '../../../model/Config';
import { useToolbarStyles } from '../../../controls';
import { useActiveConversationsToolbarStyles } from './ActiveConversationsToolbar.styles';
import { ConversationsSummaryDialog } from './ConversationSummary/ConversationsSummaryDialog';
import { MarkResolvedConfirmationDialog } from './MarkResolvedConfirmationDialog';
import { dialog } from '@microsoft/teams-js';
import { getBaseUrl } from '../../../configVariables';
import { IBrokerConnectionActiveConversationsData } from '../../../model/brokerConnections/IBrokerConnectionActiveConversationsData';

interface IActiveConversationsToolbarProps {
    isConnected: boolean;
    groupByStatus: boolean;
    onGroupByStatusChange: (value: boolean) => void;
    onTransferClick: (
        conversation: IBrokerConnectionActiveConversationsData
    ) => void;
}

export const ActiveConversationsToolbar: React.FC<
    IActiveConversationsToolbarProps
> = ({
    isConnected,
    groupByStatus,
    onGroupByStatusChange,
    onTransferClick,
}) => {
    const RESOLVE_MODAL_WIDTH = 500;
    const RESOLVE_MODAL_HEIGHT = 500;

    const toolbarButtonStyle: React.CSSProperties = { padding: '8px' };

    const { t } = useTranslation();
    const toolbarStyles = useToolbarStyles();
    const styles = useActiveConversationsToolbarStyles();
    const dispatch = useAppDispatch();

    const selectedIds = useSelector(selectActiveConversationsSelectedIds);
    const markingAsResolvedId = useSelector(selectMarkingAsResolvedId);
    const {
        data: summary,
        loading: summaryLoading,
        error: summaryError,
    } = useSelector(selectSummary);
    const {
        unreadOnly,
        searchFlightQuery,
        statusFilter,
        items,
        initiatedFromFilter,
        awaitingReplyOnly,
        arrivingSoonOnly,
    } = useSelector(selectActiveConversations);
    const config = useSelector(selectConfig);
    const isSummarisationEnabled = config.featureFlags.includes(
        FeatureFlags.conversationsSummarisation
    );

    const shiftPatternOptions: Array<{ key: ShiftPeriod; label: string }> = [
        { key: 'All', label: 'All (24 hours)' },
        { key: 'Early', label: 'Early (6am-2pm)' },
        { key: 'Late', label: 'Late (2pm-10pm)' },
        { key: 'Night', label: 'Night (10pm-6am)' },
    ];

    const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
    const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
    const [summaryOpen, setSummaryOpen] = useState<boolean>(false);
    const [selectedShift, setSelectedShift] = useState<ShiftPeriod>(() =>
        getPreviousShift()
    );

    const selectedItem = useMemo(
        () => items.find((i) => i.id === selectedIds[0]),
        [items, selectedIds]
    );

    const hasCategory = !!selectedItem?.categories?.length;
    const hasNonResolvableSelection = selectedIds.some((id) => {
        const item = items.find((item) => item.id === id);
        return item?.isAutoConnection || item?.isAutoGeneratedConversation;
    });

    const onQueryTextChange = useCallback(
        (
            _: React.ChangeEvent<HTMLInputElement> | null,
            data: InputOnChangeData
        ) => {
            dispatch(setSearchFlightQuery(data.value));
            dispatch(setCurrentPage(1)); // reset pagination when searching
        },
        [dispatch]
    );

    const onClearSearch = useCallback(
        () => onQueryTextChange(null, { value: '' }),
        [onQueryTextChange]
    );

    const onToggleUnread = useCallback(() => {
        dispatch(setUnreadOnly(!unreadOnly));
        dispatch(setCurrentPage(1));
    }, [dispatch, unreadOnly]);

    const onToggleAwaitingReply = useCallback(() => {
        dispatch(setAwaitingReplyOnly(!awaitingReplyOnly));
        dispatch(setCurrentPage(1));
    }, [dispatch, awaitingReplyOnly]);

    const onToggleArrivingSoon = useCallback(() => {
        dispatch(setArrivingSoonOnly(!arrivingSoonOnly));
        dispatch(setCurrentPage(1));
    }, [dispatch, arrivingSoonOnly]);

    const statuses = useMemo<string[]>(() => {
        const unique = Array.from(
            new Set(items.map((i) => i.conversationStatus).filter((s) => !!s))
        ) as string[];
        unique.sort((a, b) => a.localeCompare(b));
        return unique;
    }, [items]);

    const initiatedFromValues = useMemo<string[]>(() => {
        const unique = Array.from(
            new Set(items.map((i) => i.initiatedFrom).filter((s) => !!s))
        ) as string[];
        unique.sort((a, b) => a.localeCompare(b));
        return unique;
    }, [items]);

    const onStatusChange = useCallback(
        (_: any, data: { optionValue?: string }) => {
            dispatch(setStatusFilter(data.optionValue ?? ''));
            dispatch(setCurrentPage(1));
        },
        [dispatch]
    );

    const onInitiatedFromChange = useCallback(
        (_: any, data: { optionValue?: string }) => {
            dispatch(setInitiatedFromFilter(data.optionValue ?? ''));
            dispatch(setCurrentPage(1));
        },
        [dispatch]
    );

    const appliedFiltersCount = useMemo(() => {
        return (
            (statusFilter ? 1 : 0) +
            (unreadOnly ? 1 : 0) +
            (arrivingSoonOnly ? 1 : 0) +
            (initiatedFromFilter ? 1 : 0) +
            (awaitingReplyOnly ? 1 : 0)
        );
    }, [
        statusFilter,
        unreadOnly,
        arrivingSoonOnly,
        initiatedFromFilter,
        awaitingReplyOnly,
    ]);

    const filterButtonLabel = useMemo(() => {
        let label = t('Button_Filter') ?? 'Filter';
        if (appliedFiltersCount > 0) {
            label = `${label} (${appliedFiltersCount})`;
        }
        return label;
    }, [appliedFiltersCount, t]);

    const onClearAllFilters = useCallback(() => {
        dispatch(setStatusFilter(''));
        dispatch(setInitiatedFromFilter(''));
        dispatch(setUnreadOnly(false));
        dispatch(setArrivingSoonOnly(false));
        dispatch(setCurrentPage(1));
        dispatch(setAwaitingReplyOnly(false));
    }, [dispatch]);

    const openResolveModal = useCallback(async () => {
        if (selectedIds.length < 1 || !selectedIds[0]) return;

        const baseUrl = getBaseUrl();
        const resolvedId = selectedIds[0];

        dispatch(setMarkingAsResolvedId(resolvedId));

        try {
            dialog.url.open(
                {
                    url: `${baseUrl}/resolveConversation?brokerConnectionId=${resolvedId}&hasCategory=true&fromToolbar=true`,
                    size: {
                        width: RESOLVE_MODAL_WIDTH,
                        height: RESOLVE_MODAL_HEIGHT,
                    },
                    title: t('Dialog_MarkResolved_Title'),
                },
                (result) => {
                    // Modal was dismissed/closed without submission
                    if (result?.result === undefined) {
                        dispatch(setMarkingAsResolvedId(null));
                    }
                }
            );
        } catch (error) {
            console.error('Error with resolve modal:', error);
            dispatch(setMarkingAsResolvedId(null));
        }
    }, [selectedIds, dispatch, t]);

    const handleMarkResolvedClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();

            if (selectedIds.length < 1) return;

            if (hasCategory) {
                openResolveModal();
            } else {
                setConfirmOpen(true);
            }
        },
        [selectedIds.length, hasCategory, openResolveModal]
    );

    const handleSummariseClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();

            const previousShift = getPreviousShift();
            setSelectedShift(previousShift);
            setSummaryOpen(true);
            dispatch(fetchSummary({ shift: previousShift }));
        },
        [dispatch]
    );

    const handleConfirmMarkResolved = useCallback(async () => {
        if (selectedIds.length < 1) return;

        await dispatch(markConversationsResolved(selectedIds));
        setConfirmOpen(false);
    }, [dispatch, selectedIds]);

    useEffect(() => {
        if (
            markingAsResolvedId &&
            !items.find((item) => item.id === markingAsResolvedId)
        ) {
            dispatch(setMarkingAsResolvedId(null));
        }
    }, [items, markingAsResolvedId, dispatch]);

    const connectionStatus = useMemo(
        () => ({
            isConnected,
            appearance: isConnected ? ('filled' as const) : ('tint' as const),
            color: isConnected ? ('success' as const) : ('subtle' as const),
            statusText: isConnected ? t('SignalR_Live') : t('SignalR_Offline'),
            tooltipText: isConnected
                ? t('SignalR_Connected')
                : t('SignalR_Disconnected'),
        }),
        [isConnected, t]
    );

    const handleDialogCancel = useCallback(() => {
        setConfirmOpen(false);
    }, []);

    const handleSummaryClose = useCallback(() => {
        setSummaryOpen(false);
        dispatch(clearSummary());
    }, [dispatch]);

    const handleShiftChange = useCallback(
        (_: any, data: { optionValue?: string }) => {
            const shift =
                (data.optionValue as ShiftPeriod) ?? getPreviousShift();
            setSelectedShift(shift);
            dispatch(fetchSummary({ shift }));
        },
        [dispatch]
    );

    const handleTransferClick = useCallback(() => {
        if (!selectedItem) {
            return;
        }

        onTransferClick(selectedItem);
    }, [selectedItem, onTransferClick]);

    return (
        <>
            <Toolbar
                aria-label="Active conversations"
                className={toolbarStyles.toolbar}
            >
                <ToolbarGroup
                    className={toolbarStyles.toolbarGroup}
                    role="presentation"
                >
                    {!hasNonResolvableSelection && (
                        <ToolbarButton
                            className={styles.markedResolvedBtn}
                            aria-label={t('Button_MarkResolved')}
                            appearance="primary"
                            disabled={!selectedItem || !!markingAsResolvedId}
                            icon={
                                markingAsResolvedId ? (
                                    <Spinner size="tiny" />
                                ) : (
                                    <CheckmarkCircle24Regular />
                                )
                            }
                            onClick={handleMarkResolvedClick}
                            style={toolbarButtonStyle}
                        >
                            {t('Button_MarkResolved')}
                        </ToolbarButton>
                    )}
                    <ToolbarButton
                        className={styles.markedResolvedBtn}
                        aria-label={t('ActiveConversations_TransferButton')}
                        disabled={
                            !selectedItem ||
                            !!selectedItem.isTransferred ||
                            selectedItem.isAutoConnection ||
                            true // TODO: Hidden until the auto conversation transfer is worked out
                        }
                        icon={<ArrowForwardRegular />}
                        onClick={handleTransferClick}
                        style={{ ...toolbarButtonStyle, display: 'none' }}
                    >
                        {t('ActiveConversations_TransferButton')}
                    </ToolbarButton>
                </ToolbarGroup>
                <ToolbarGroup
                    role="presentation"
                    className={mergeClasses(
                        toolbarStyles.toolbarGroup,
                        toolbarStyles.toolbarRightGroup
                    )}
                >
                    {isSummarisationEnabled && (
                        <ToolbarButton
                            className={styles.summarisedBtn}
                            aria-label={t('Button_Summarise')}
                            appearance="subtle"
                            onClick={handleSummariseClick}
                            icon={<WindowBulletListFilled />}
                            style={toolbarButtonStyle}
                        >
                            {t('Button_Summarise')}
                        </ToolbarButton>
                    )}
                    <Tooltip
                        content={connectionStatus.tooltipText}
                        relationship="label"
                    >
                        <Badge
                            appearance={connectionStatus.appearance}
                            icon={<Circle12Filled />}
                            color={connectionStatus.color}
                            className={styles.statusIndicator}
                        >
                            {connectionStatus.statusText}
                        </Badge>
                    </Tooltip>
                    <div className={styles.filterSearchGroup}>
                        <Switch
                            checked={groupByStatus}
                            label={t('Toggle_StatusGrouping')}
                            onChange={(_, data) =>
                                onGroupByStatusChange(data.checked ?? false)
                            }
                        />
                        <Popover
                            open={filtersOpen}
                            onOpenChange={(_, data) => {
                                setFiltersOpen(data.open);
                            }}
                        >
                            <PopoverTrigger disableButtonEnhancement>
                                <ToolbarButton
                                    className={styles.filterBtn}
                                    aria-label={t('Button_Filter')}
                                    appearance="subtle"
                                    icon={<Filter24Regular />}
                                >
                                    {filterButtonLabel}
                                </ToolbarButton>
                            </PopoverTrigger>
                            <PopoverSurface>
                                <div className={styles.filterPopover}>
                                    <div className={styles.clearFilters}>
                                        <Link
                                            appearance="subtle"
                                            disabled={appliedFiltersCount === 0}
                                            onClick={onClearAllFilters}
                                        >
                                            {t('Button_Filter_ClearAll')}
                                        </Link>
                                    </div>
                                    <div className={styles.dropdownFilter}>
                                        <Label>
                                            {t('Toolbar_Status_Filter')}
                                        </Label>
                                        <Dropdown
                                            aria-label="Filter by status"
                                            value={
                                                statusFilter
                                                    ? statusFilter
                                                    : t(
                                                          'Toolbar_Status_Filter_All'
                                                      )
                                            }
                                            selectedOptions={[statusFilter]}
                                            onOptionSelect={onStatusChange}
                                        >
                                            <Option value="">
                                                {t('Toolbar_Status_Filter_All')}
                                            </Option>
                                            {statuses.map((s) => (
                                                <Option key={s} value={s}>
                                                    {s}
                                                </Option>
                                            ))}
                                        </Dropdown>
                                    </div>
                                    <div className={styles.dropdownFilter}>
                                        <Label>
                                            {t('Toolbar_InitiatedFrom_Filter')}
                                        </Label>
                                        <Dropdown
                                            aria-label="Filter by initiated from"
                                            value={
                                                initiatedFromFilter
                                                    ? initiatedFromFilter
                                                    : t(
                                                          'Toolbar_InitiatedFrom_Filter_All'
                                                      )
                                            }
                                            selectedOptions={[
                                                initiatedFromFilter,
                                            ]}
                                            onOptionSelect={
                                                onInitiatedFromChange
                                            }
                                        >
                                            <Option value="">
                                                {t(
                                                    'Toolbar_InitiatedFrom_Filter_All'
                                                )}
                                            </Option>
                                            {initiatedFromValues.map((s) => (
                                                <Option key={s} value={s}>
                                                    {s}
                                                </Option>
                                            ))}
                                        </Dropdown>
                                    </div>
                                    <div className={styles.checkboxFilter}>
                                        <Checkbox
                                            checked={unreadOnly}
                                            onChange={() => onToggleUnread()}
                                        />
                                        <Label>
                                            {t('Toolbar_Unread_Filter')}
                                        </Label>
                                    </div>
                                    <div className={styles.checkboxFilter}>
                                        <Checkbox
                                            checked={awaitingReplyOnly}
                                            onChange={() =>
                                                onToggleAwaitingReply()
                                            }
                                        />
                                        <Label>
                                            {t('Toolbar_Awaiting_Reply_Filter')}
                                        </Label>
                                    </div>
                                    <div className={styles.checkboxFilter}>
                                        <Checkbox
                                            checked={arrivingSoonOnly}
                                            onChange={() =>
                                                onToggleArrivingSoon()
                                            }
                                        />
                                        <Label>
                                            {t('Toolbar_Arriving_Soon_Filter')}
                                        </Label>
                                    </div>
                                </div>
                            </PopoverSurface>
                        </Popover>
                        <Input
                            className={mergeClasses(
                                toolbarStyles.searchBar,
                                styles.searchInput
                            )}
                            placeholder={t('SearchFlightsInputPlaceholder')!}
                            size="small"
                            contentAfter={
                                <Button
                                    appearance="transparent"
                                    onClick={onClearSearch}
                                    icon={
                                        !searchFlightQuery ? (
                                            <Search24Regular />
                                        ) : (
                                            <Dismiss24Regular />
                                        )
                                    }
                                />
                            }
                            onChange={onQueryTextChange}
                            value={searchFlightQuery}
                        />
                    </div>
                </ToolbarGroup>
            </Toolbar>

            <MarkResolvedConfirmationDialog
                open={confirmOpen}
                hasCategory={hasCategory}
                onConfirm={handleConfirmMarkResolved}
                onCancel={handleDialogCancel}
            />

            <ConversationsSummaryDialog
                open={summaryOpen}
                loading={summaryLoading}
                error={summaryError}
                summary={summary}
                selectedShift={selectedShift}
                shiftOptions={shiftPatternOptions}
                onShiftChange={handleShiftChange}
                onClose={handleSummaryClose}
            />
        </>
    );
};
